import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, MapPin, Phone, MessageCircle, Mail } from 'lucide-react';

const LOGO_URL = 'https://storage.stikes-hi.ac.id/stikes-hi-media/3fbebd76-22d3-4641-a12f-ec3c51280150.png';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.19a8.18 8.18 0 0 0 4.78 1.52V6.27a4.85 4.85 0 0 1-1.01-.42z" />
  </svg>
);

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0F0F1A] text-gray-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Col 1: Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="Logo STIKES HI" className="h-12 w-12 object-contain" />
              <div>
                <div className="text-white font-bold text-lg leading-tight font-heading">STIKES HI</div>
                <div className="text-gray-400 text-xs">Harapan Ibu Jambi</div>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Sekolah Tinggi Ilmu Kesehatan Harapan Ibu Jambi — Unggul, Berkarakter, dan Berdaya Saing dalam Ilmu Kesehatan untuk Indonesia.
            </p>
            <div className="flex gap-2.5">
              {[
                { icon: <Instagram size={17} />, href: 'https://www.instagram.com/stikesharapanibu/', label: 'Instagram' },
                { icon: <Facebook size={17} />, href: 'https://www.facebook.com/stikeshiofficial', label: 'Facebook' },
                { icon: <Youtube size={17} />, href: 'https://www.youtube.com/@STIKESHIJAMBI', label: 'YouTube' },
                { icon: <TikTokIcon />, href: 'https://www.tiktok.com/@stikeshijambi', label: 'TikTok' },
              ].map(({ icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center
                    text-gray-400 hover:bg-[#7B2FBE] hover:text-white transition-all duration-300 hover:scale-110"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Tentang Kami */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider font-heading">Tentang Kami</h3>
            <ul className="space-y-3">
              {[
                { label: 'Sejarah Kampus', path: '/sejarah' },
                { label: 'Visi & Misi', path: '/visi-misi' },
                { label: 'Struktur Organisasi', path: '/struktur-organisasi' },
                { label: 'Logo Kampus', path: '/logo' },
                { label: 'Mars STIKES HI', path: '/mars-stikes' },
                { label: 'Dosen & Tendik', path: '/dosen-tendik' },
                { label: 'Humas', path: '/unit/humas' },
                { label: 'Unit IT', path: '/unit/it' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-400 hover:text-[#9333EA] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Akademik & Fasilitas */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider font-heading">Akademik & Fasilitas</h3>
            <ul className="space-y-3">
              {[
                { label: 'Program Studi', path: '/program-studi' },
                { label: 'Kalender Akademik', path: '/kalender' },
                { label: 'Sarana & Prasarana', path: '/sarana-prasarana' },
                { label: 'Agenda & Event', path: '/agenda' },
                { label: 'Berita & Pengumuman', path: '/berita' },
                { label: 'Download File', path: '/download' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-400 hover:text-[#9333EA] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Kontak */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider font-heading">Kontak</h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin size={16} className="text-[#7B2FBE] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400 leading-relaxed">
                  Jl. Tarmizi Kadir No.71<br />Pakuan Baru 36132, Jambi
                </span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-[#7B2FBE] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">(0741) 7552270</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle size={16} className="text-[#7B2FBE] mt-0.5 flex-shrink-0" />
                <a href="https://wa.me/6282177711609" className="text-sm text-gray-400 hover:text-[#9333EA] transition-colors">
                  082177711609
                </a>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-[#7B2FBE] mt-0.5 flex-shrink-0" />
                <a href="mailto:surat@stikes-hi.ac.id" className="text-sm text-gray-400 hover:text-[#9333EA] transition-colors">
                  surat@stikes-hi.ac.id
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800/80 pt-8">
          <p className="text-center text-sm text-gray-500">
            © {year} STIKES Harapan Ibu Jambi. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
